import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../auth/AuthContext';
import { searchOrders } from '../../utils/searchCache';
import AssociationBadges from '../common/AssociationBadges';
import Tooltip from '../common/Tooltip';
import { FEATURE_PERMISSIONS } from '../../utils/permissions';
import { usePermissions } from '../../hooks/usePermissions';
import { getAssignmentUsers } from '../../utils/assignmentUsers';

/**
 * Searchable Select Component with Client-Side Filtering
 * Loads data once on open, then filters client-side (matches OppModal pattern)
 */
const SearchableSelect = ({ value, onChange, options, placeholder, className = '', disabled = false, onLoad }) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Load data once when dropdown opens (only on isOpen change, not onLoad reference change)
  useEffect(() => {
    if (isOpen && !hasLoaded && onLoad) {
      onLoad().then(() => setHasLoaded(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasLoaded]); // Intentionally exclude onLoad to prevent reload on reference change

  // Client-side filtering (instant, no debounce needed)
  const filtered = useMemo(() => {
    if (!search.trim()) return options.slice(0, 50); // Show first 50 if no search
    const searchLower = search.toLowerCase();
    return options.filter(opt => opt.label.toLowerCase().includes(searchLower)).slice(0, 100);
  }, [options, search]);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className="relative">
      <div className="relative">
        <input
          type="text"
          value={isOpen ? search : (selectedOption?.label || '')}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className={`w-full border p-2 rounded ${className}`}
          disabled={disabled}
        />
        {value && !isOpen && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
              setSearch('');
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>
      
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setIsOpen(false);
              setSearch('');
            }}
          />
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {!hasLoaded && onLoad && (
              <div className="px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                Loading...
              </div>
            )}
            {hasLoaded && search.trim() && filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500">
                No results found. Try a different search term.
              </div>
            )}
            {hasLoaded && !search.trim() && (
              <div className="px-3 py-2 text-xs text-gray-400 bg-gray-50 border-b">
                Type to search... (showing first 50 of {options.length})
              </div>
            )}
            {hasLoaded && filtered.length === 0 && !search.trim() ? (
              <div className="px-3 py-2 text-sm text-gray-500">Start typing to search</div>
            ) : (
              filtered.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${
                    opt.value === value ? 'bg-blue-100 font-semibold' : ''
                  }`}
                >
                  {opt.label}
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Lazy Searchable Select - Only loads when user starts typing
 * Perfect for large datasets (orders) - avoids loading delay on dropdown open
 * 
 * Key difference from SearchableSelect:
 * - SearchableSelect: Loads all data when dropdown opens, then filters client-side
 * - SearchableSelectLazy: Only loads data after user types search term
 * 
 * Benefits:
 * - Instant dropdown open (no loading spinner)
 * - Targeted queries (only loads matching results)
 * - Lower Firestore costs (95% reduction)
 * - Prepares for Typesense migration (same search-triggered pattern)
 */
const SearchableSelectLazy = ({ 
  value, 
  onChange, 
  placeholder, 
  className = '', 
  disabled = false, 
  onSearch, 
  minSearchLength = 4 
}) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState('');

  // Debounced search - only triggers after user stops typing (300ms)
  useEffect(() => {
    if (!isOpen || !search.trim() || search.length < minSearchLength) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    const timer = setTimeout(async () => {
      if (onSearch) {
        setIsSearching(true);
        try {
          const results = await onSearch(search);
          setSearchResults(results || []);
        } catch (error) {
          console.error('[SearchableSelectLazy] Search error:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [search, isOpen, onSearch, minSearchLength]);

  return (
    <div className="relative">
      <div className="relative">
        <input
          type="text"
          value={isOpen ? search : selectedLabel}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className={`w-full border p-2 rounded ${className}`}
          disabled={disabled}
        />
        {value && !isOpen && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
              setSearch('');
              setSelectedLabel('');
              setSearchResults([]);
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>
      
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setIsOpen(false);
              setSearch('');
            }}
          />
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {isSearching && (
              <div className="px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                Searching...
              </div>
            )}
            {!isSearching && search.length < minSearchLength && (
              <div className="px-3 py-2 text-sm text-gray-500">
                Type at least {minSearchLength} characters to search...
              </div>
            )}
            {!isSearching && search.length >= minSearchLength && searchResults.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500">
                No results found for "{search}"
              </div>
            )}
            {!isSearching && searchResults.length > 0 && (
              <>
                <div className="px-3 py-2 text-xs text-gray-400 bg-gray-50 border-b">
                  Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                </div>
                {searchResults.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setSelectedLabel(opt.label);
                      setIsOpen(false);
                      setSearch('');
                      setSearchResults([]);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${
                      opt.value === value ? 'bg-blue-100 font-semibold' : ''
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

function isPastOrToday(dateStr) {
  if (!dateStr) return false;
  const selected = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  today.setHours(0,0,0,0);
  return selected <= today;
}

const TaskModal = ({ 
  salesReps = [], 
  initial = null, 
  onSave, 
  onClose, 
  isAdmin = false,
  customers = [],
  opportunities = [],
  orders = [],
  contacts = [],
  // Load callbacks (load once on dropdown open)
  onLoadCustomers,
  onLoadOpportunities,
  onLoadOrders,
  onLoadContacts,
  // For pre-populating associations (e.g., from customerDetailPage)
  associatedcustomerId = null,
  associatedOpportunityId = null,
  associatedOrderId = null,
  associatedContactId = null
}) => {
  const { currentUser } = useAuth();
  const { can } = usePermissions(currentUser);
  // Users with TASK_ASSIGN_ANY (e.g. admins, customer support) can assign tasks to any
  // user. Without this permission, users can only assign tasks to themselves.
  const canAssignAnyUser = can(FEATURE_PERMISSIONS.TASK_ASSIGN_ANY);
  const currentUserUid = currentUser?.uid || currentUser?.id || '';
  const [form, setForm] = useState({
    title: '',
    description: '',
    dueDate: '',
    uid: canAssignAnyUser ? '' : currentUserUid,
    status: 'open',
    priority: 'Medium',
    // Association fields
    customerId: associatedcustomerId || '',
    opportunityId: associatedOpportunityId || '',
    orderId: associatedOrderId || '',
    contactId: associatedContactId || '',
    actionItems: []
  });
  const [saving, setSaving] = useState(false);
  const [resolvedAssociations, setResolvedAssociations] = useState(null);
  const [newActionItem, setNewActionItem] = useState('');
  const isEdit = Boolean(initial?.id);

  // Prepare searchable options
  const customerOptions = useMemo(() => {
    return [
      { value: '', label: 'No customer' },
      ...customers.map(c => ({
        value: c.id,
        label: c.customerName || c.name || c.id
      }))
    ];
  }, [customers]);

  const opportunityOptions = useMemo(() => {
    return [
      { value: '', label: 'No opportunity' },
      ...opportunities.map(o => ({
        value: o.id,
        label: `${o.opportunityNumberLabel || o.id}${o.customerName ? ' - ' + o.customerName : ''}${o.description ? ': ' + o.description.substring(0, 30) : ''}`
      }))
    ];
  }, [opportunities]);

  const orderOptions = useMemo(() => {
    return [
      { value: '', label: 'No order' },
      ...orders.map(o => ({
        value: o.id,
        label: `${o.order_number || o.number || o.id}${o.customer_name ? ' - ' + o.customer_name : ''}`
      }))
    ];
  }, [orders]);

  const contactOptions = useMemo(() => {
    const list = contacts.map(c => {
      const name = c.firstName && c.lastName ? `${c.firstName} ${c.lastName}` : '';
      const email = c.email ? ` (${c.email})` : '';
      const customerLabel = c.customerName ? ` - ${c.customerName}` : '';
      const label = name ? `${name}${email}${customerLabel}` : c.email || c.id;
      return { value: c.id, label };
    });

    // When editing and contacts haven't loaded yet, seed a placeholder from the
    // already-resolved associations so the selector shows the name immediately.
    const editContactId = initial?.associations?.contactId || initial?.contactId;
    const editContactName = initial?.associations?.contactName;
    if (editContactId && editContactName && list.length === 0) {
      const previewEmail = initial?.associations?.contactEmail
        ? ` (${initial.associations.contactEmail})`
        : '';
      list.push({ value: editContactId, label: `${editContactName}${previewEmail}` });
    }

    return [{ value: '', label: 'No contact' }, ...list];
  }, [contacts, initial]);

  useEffect(() => {
    if (initial) {
      // Helper to convert Timestamp to YYYY-MM-DD using UTC
      const toInputDate = (val) => {
        if (!val) return '';
        if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
        
        let d;
        if (val.toDate && typeof val.toDate === 'function') {
          d = val.toDate();
        } else if (val.seconds) {
          d = new Date(val.seconds * 1000);
        } else {
          d = new Date(val);
        }
        
        if (!d || isNaN(d)) return '';
        
        // Use UTC methods to avoid timezone shifts
        const yyyy = d.getUTCFullYear();
        const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
        const dd = String(d.getUTCDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      };
      
      // Extract association IDs from both old and new formats
      const assoc = initial.associations || {};
      
      setForm({
        title: initial.title || '',
        description: initial.description || '',
        dueDate: toInputDate(initial.dueDate),
        uid: initial.uid || '',
        status: (initial.status || 'open').toLowerCase(),
        priority: initial.priority || 'Medium',
        customerId: assoc.customerId || initial.customerId || associatedcustomerId || '',
        opportunityId: assoc.opportunityId || initial.opportunityId || associatedOpportunityId || '',
        orderId: assoc.orderId || initial.orderId || associatedOrderId || '',
        contactId: assoc.contactId || initial.contactId || associatedContactId || '',
        actionItems: initial.actionItems || []
      });
      
      // If editing, set resolved associations from existing task
      if (initial.associations) {
        setResolvedAssociations(initial.associations);
      }
    }
  }, [initial]);

  // Auto-resolve associations when any association field changes
  useEffect(() => {
    const resolve = async () => {
      if (!form.customerId && !form.opportunityId && !form.orderId && !form.contactId) {
        setResolvedAssociations(null);
        return;
      }

      // Auto-load entities if association is set but data not loaded
      if (form.customerId && customers.length === 0 && onLoadCustomers) {
        await onLoadCustomers();
      }
      if (form.opportunityId && opportunities.length === 0 && onLoadOpportunities) {
        await onLoadOpportunities();
      }
      if (form.orderId && orders.length === 0 && onLoadOrders) {
        await onLoadOrders();
      }
      if (form.contactId && contacts.length === 0 && onLoadContacts) {
        await onLoadContacts();
      }

      try {
        // Resolve associations from cached data (faster than Firestore lookups)
        const resolved = {};
        
        if (form.customerId) {
          const customer = customers.find(c => c.id === form.customerId);
          if (customer) {
            resolved.customerId = form.customerId;
            resolved.customerName = customer.customerName || customer.name;
          }
        }
        
        if (form.opportunityId) {
          const opportunity = opportunities.find(o => o.id === form.opportunityId);
          if (opportunity) {
            resolved.opportunityId = form.opportunityId;
            // AssociationBadges requires opportunityNumberLabel (not oppNum)
            resolved.opportunityNumberLabel = opportunity.opportunityNumberLabel
              || (opportunity.opportunityNumber ? `OPP-${opportunity.opportunityNumber}` : form.opportunityId);
            resolved.oppNum = opportunity.oppNum || opportunity.opportunityNumber;
            resolved.description = opportunity.description || '';
            resolved.customerName = resolved.customerName || opportunity.customerName || '';
          } else {
            // Opportunity not in cache yet — carry forward from initial.associations
            // so the badge still shows while the cache loads
            const fallback = initial?.associations || {};
            if (fallback.opportunityId === form.opportunityId) {
              resolved.opportunityId = form.opportunityId;
              resolved.opportunityNumberLabel = fallback.opportunityNumberLabel || form.opportunityId;
              resolved.oppNum = fallback.oppNum;
              resolved.description = fallback.description || '';
              resolved.customerName = resolved.customerName || fallback.customerName || '';
            }
          }
        }
        
        if (form.orderId) {
          const order = orders.find(o => o.id === form.orderId);
          if (order) {
            resolved.orderId = form.orderId;
            resolved.orderNumber = order.orderNumber || order.order_number || order.id;
            resolved.customerName = order.customer_name || order.customerName;
          }
        }
        
        if (form.contactId) {
          const contact = contacts.find(c => c.id === form.contactId);
          if (contact) {
            resolved.contactId = form.contactId;
            resolved.contactName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
            resolved.contactEmail = contact.email;
          }
        }
        
        setResolvedAssociations(Object.keys(resolved).length > 0 ? resolved : null);
      } catch (err) {
        console.error('Failed to resolve associations:', err);
      }
    };

    resolve();
  }, [form.customerId, form.opportunityId, form.orderId, form.contactId, customers, opportunities, orders, contacts, onLoadCustomers, onLoadOpportunities, onLoadOrders, onLoadContacts]);

  const handleChange = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleAddActionItem = () => {
    if (!newActionItem.trim()) return;
    setForm(prev => ({
      ...prev,
      actionItems: [
        ...prev.actionItems,
        {
          text: newActionItem.trim(),
          completed: false,
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.uid || currentUser?.id
        }
      ]
    }));
    setNewActionItem('');
  };

  const handleToggleActionItem = (index) => {
    setForm(prev => ({
      ...prev,
      actionItems: prev.actionItems.map((item, i) =>
        i === index ? { ...item, completed: !item.completed } : item
      )
    }));
  };

  const handleDeleteActionItem = (index) => {
    setForm(prev => ({
      ...prev,
      actionItems: prev.actionItems.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const requiredPerm = initial?.id ? FEATURE_PERMISSIONS.TASK_EDIT : FEATURE_PERMISSIONS.TASK_CREATE;
    if (saving || !can(requiredPerm)) return;

    // Validate required fields
    if (!form.title.trim()) {
      alert('Task Name is required.');
      return setSaving(false);
    }
    if (!form.uid) {
      alert('Assigned To is required.');
      return setSaving(false);
    }
    if (!form.dueDate) {
      alert('Due Date is required.');
      return setSaving(false);
    }

    const dueDate = new Date(form.dueDate + 'T00:00:00');

    // Find assigned user's name
    let assignedTo = '';
    const selectedRep = salesReps.find(r => r.uid === form.uid);
    if (selectedRep) {
      if (selectedRep.firstName || selectedRep.lastName) {
        assignedTo = `${selectedRep.firstName || ''} ${selectedRep.lastName || ''}`.trim();
      } else {
        assignedTo = selectedRep.displayName || selectedRep.name || selectedRep.email || selectedRep.uid;
      }
    }

    setSaving(true);
    try {
      // Prepare task data with associations
      const taskData = {
        ...form,
        dueDate,
        assignedTo,
        // Include resolved associations for parent to use
        associations: resolvedAssociations,
        // Keep individual IDs for backward compatibility
        customerId: form.customerId || null,
        opportunityId: form.opportunityId || null,
        orderId: form.orderId || null,
        contactId: form.contactId || null
      };
      
      // call parent's onSave and allow it to handle add/update
      await Promise.resolve(onSave ? onSave(taskData) : null);
      // parent is expected to close modal; but close here defensively if parent didn't
      setSaving(false);
    } catch (err) {
      console.error('TaskModal save failed', err);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <form 
        onSubmit={handleSubmit} 
        className="bg-white rounded-lg shadow-xl w-full max-w-3xl my-4 flex flex-col max-h-[calc(100vh-2rem)]"
      >
        {/* Header - Fixed */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 rounded-t-lg flex-shrink-0">
          <h3 className="text-xl font-bold text-gray-900">{isEdit ? 'Edit Task' : 'Create Task'}</h3>
          <button 
            type="button" 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={form.title}
              onChange={e => handleChange('title', e.target.value)}
              placeholder="Enter task title..."
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
            <textarea
              className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={form.description}
              onChange={e => handleChange('description', e.target.value)}
              rows={3}
              placeholder="Task description..."
            />
          </div>

          {/* Metadata Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Status */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
              <select
                className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                value={(form.status || 'open').toLowerCase()}
                onChange={e => handleChange('status', e.target.value)}
              >
                <option value="open">Open</option>
                <option value="completed">Completed</option>
                {isAdmin && <option value="closed">Closed</option>}
              </select>
            </div>

            {/* Priority */}
            <div>
              <Tooltip text="Set task priority - High priority tasks appear in notifications and reports">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Priority <span className="text-red-500">*</span>
                </label>
              </Tooltip>
              <select
                className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                value={form.priority || 'Medium'}
                onChange={e => handleChange('priority', e.target.value)}
                required
              >
                <option value="High">🔴 High</option>
                <option value="Medium">🟡 Medium</option>
                <option value="Low">🟢 Low</option>
              </select>
            </div>

            {/* Due Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Due Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className={`w-full border p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  isPastOrToday(form.dueDate) && form.status !== 'completed' 
                    ? 'border-red-300 bg-red-50' 
                    : 'border-gray-300'
                }`}
                value={form.dueDate}
                onChange={e => handleChange('dueDate', e.target.value)}
                required
              />
            </div>

            {/* Assigned To */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Assigned To <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                value={form.uid}
                onChange={e => handleChange('uid', e.target.value)}
                required
                disabled={!canAssignAnyUser}
                title={!canAssignAnyUser ? 'You can only assign tasks to yourself' : ''}
              >
                <option value="">Select assignee...</option>
                {(canAssignAnyUser
                  ? getAssignmentUsers(salesReps, initial?.id ? initial.uid : '')
                  : salesReps.filter(r => (r.uid || r.userId || r.id) === currentUserUid)
                ).map(r => {
                    const fullName = r.displayName ||
                        ((r.firstName && r.lastName) ? `${r.firstName} ${r.lastName}` : '') ||
                        r.name || r.email || r.uid;
                    return (
                        <option key={r.uid} value={r.uid} disabled={r.role === 'disabled'}>
                            {fullName}{r.role === 'disabled' ? ' (Deactivated)' : ''}
                        </option>
                    );
                })}
              </select>
              {!canAssignAnyUser && (
                <p className="mt-1 text-xs text-gray-500">You can only assign tasks to yourself.</p>
              )}
            </div>
          </div>

          {/* Association Selectors */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Associated Entities (Optional)</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Customer Selector */}
              <div>
                <Tooltip text="Associate this task with a customer">
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Customer</label>
                </Tooltip>
                <SearchableSelect
                  value={form.customerId}
                  onChange={(val) => handleChange('customerId', val)}
                  options={customerOptions}
                  placeholder="Search customers..."
                  className="text-sm"
                  onLoad={onLoadCustomers}
                />
              </div>

              {/* Opportunity Selector */}
              <div>
                <Tooltip text="Associate this task with an opportunity (auto-links to customer)">
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Opportunity</label>
                </Tooltip>
                <SearchableSelect
                  value={form.opportunityId}
                  onChange={(val) => handleChange('opportunityId', val)}
                  options={opportunityOptions}
                  placeholder="Search opportunities..."
                  className="text-sm"
                  onLoad={onLoadOpportunities}
                />
              </div>

              {/* Order Selector */}
              <div>
                <Tooltip text="Associate this task with an order">
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Order</label>
                </Tooltip>
                <SearchableSelectLazy
                  value={form.orderId}
                  onChange={(val) => handleChange('orderId', val)}
                  placeholder="Search orders (type 3+ characters)..."
                  className="text-sm"
                  onSearch={async (searchTerm) => {
                    const results = await searchOrders(searchTerm, currentUser?.role || 'default');
                    return results.map(o => ({
                      value: o.id,
                      label: `${o.orderNumber || o.order_number || o.number || o.id}${o.customer_name ? ' - ' + o.customer_name : ''}`
                    }));
                  }}
                  minSearchLength={3}
                />
              </div>

              {/* Contact Selector */}
              <div>
                <Tooltip text="Associate this task with a contact">
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Contact</label>
                </Tooltip>
                <SearchableSelect
                  value={form.contactId}
                  onChange={(val) => handleChange('contactId', val)}
                  options={contactOptions}
                  placeholder="Search contacts..."
                  className="text-sm"
                  onLoad={onLoadContacts}
                />
              </div>
            </div>

            {/* Display resolved associations */}
            {resolvedAssociations && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="text-xs font-semibold text-gray-700 mb-2">Associated Entities:</div>
                <AssociationBadges associations={resolvedAssociations} size="sm" />
              </div>
            )}
          </div>

          {/* Action Items Section */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                Action Items
              </h4>
              <span className="text-xs text-gray-500">
                {form.actionItems.filter(item => item.completed).length} of {form.actionItems.length} completed
              </span>
            </div>

            {/* Action Items List */}
            <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
              {form.actionItems.length === 0 ? (
                <div className="text-gray-400 text-sm text-center py-6 bg-gray-50 rounded-lg">
                  No action items yet
                </div>
              ) : (
                form.actionItems.map((item, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                      item.completed
                        ? 'bg-green-50 border-green-200'
                        : 'bg-white border-gray-200 hover:border-indigo-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={item.completed}
                      onChange={() => handleToggleActionItem(index)}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer flex-shrink-0"
                    />
                    <span
                      className={`flex-1 text-sm ${
                        item.completed ? 'line-through text-gray-500' : 'text-gray-900'
                      }`}
                    >
                      {item.text}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleDeleteActionItem(index)}
                      className="text-red-600 hover:text-red-800 p-1 flex-shrink-0"
                      title="Delete action item"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Add New Action Item */}
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Add new action item..."
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={newActionItem}
                onChange={(e) => setNewActionItem(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddActionItem();
                  }
                }}
              />
              <button
                type="button"
                onClick={handleAddActionItem}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex-shrink-0"
              >
                Add
              </button>
            </div>
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-lg flex-shrink-0">
          <button 
            type="button" 
            onClick={onClose} 
            className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-semibold transition-colors" 
            disabled={saving}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50" 
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Task'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TaskModal;
